<template>
    <button :class="[buttonWrapperClasses, buttonPseudoClasses]" :disabled="disabled">
        <div :class="buttonClasses" :disabled="disabled">
            <!-- Emoticon -->
            <Emoticon v-if="emoticon && !label" :type="emoticon" :class="emoticonClassesFull" />
            <Emoticon v-else-if="emoticon" :type="emoticon" :class="emoticonClasses" />

            <Icon v-if="icon && iconPosition === 'left'" :type="icon" :filled="iconFilled" :class="iconClasses" />
            <span v-if="label" class="truncate">{{ props.loading && props.loadingLabel ? props.loadingLabel : props.label }}</span>
            <Icon v-if="icon && iconPosition === 'right'" :type="icon" :filled="iconFilled" :class="iconClasses" />

            <!-- Badge -->
            <Badge
                v-if="badgeLabel"
                :label="badgeLabel"
                :variant="disabled ? 'neutral' : badgeVariant"
                size="sm"
                :class="['cursor-[inherit] -mr-1', disabled ? 'text-inactive' : '']"
            />
        </div>
    </button>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { Icon, IconType } from '../icons'
    import { Badge, Emoticon } from '../elements'
    import { twMerge } from 'tailwind-merge'

    const props = withDefaults(
        defineProps<{
            label?: string
            loadingLabel?: string
            variant?: 'primary' | 'secondary' | 'blue' | 'light' | 'dark' | 'outline' | 'error' | 'muted' | 'transparent'
            size?: 'sm' | 'md' | 'lg' | 'xl'
            icon?: IconType | undefined
            iconFilled?: boolean
            iconPosition?: 'left' | 'right'
            disabled?: boolean
            loading?: boolean
            badgeLabel?: string | number
            badgeVariant?: 'outline' | 'light' | 'neutral' | 'alert' | 'success' | 'warning' | 'dark'
            emoticon?: 'success' | 'warning' | 'error' | 'neutral' | 'neutral-light' | undefined
        }>(),
        {
            size: 'md',
            variant: 'primary',
            iconPosition: 'left',
            iconFilled: false,
            disabled: false,
            loading: false,
            badgeLabel: undefined,
            badgeVariant: 'alert'
        }
    )

    const buttonWrapperClasses = computed(() => {
        const baseClasses =
            'relative overflow-hidden inline-flex flex-row font-semibold items-center justify-center rounded-full transition-all duration-300 cursor-pointer select-none z-0'

        // Outline classes for both Tailwind v3 and v4. Outline-none has been changed to outline-hidden. In v4 outline-none just sets `outline-style: none`, so some extra classes are needed to make it work in both versions.
        const outlineClasses =
            'outline-none outline-hidden -outline-offset-1 focus:outline-none focus:outline-hidden focus:-outline-offset-1 focus-visible:outline-1 focus-visible:outline-solid focus-visible:-outline-offset-1'

        const sizeClasses = {
            sm: (props.emoticon || props.icon) && !props.label ? 'min-h-8 min-w-8' : 'px-3 py-2 min-h-8 text-xs gap-2',
            md: (props.emoticon || props.icon) && !props.label ? 'min-h-10 min-w-10' : 'px-5 py-2 min-h-10 text-sm gap-3',
            lg: (props.emoticon || props.icon) && !props.label ? 'min-h-12 min-w-12' : 'px-6 py-2 min-h-12 text-base gap-3',
            xl: (props.emoticon || props.icon) && !props.label ? 'min-h-14 min-w-14' : 'px-7 py-3 min-h-14 text-lg gap-4'
        }

        const variantClasses = {
            primary: props.emoticon && !props.label ? 'bg-transparent' : 'text-default bg-green-500 hover:bg-green-300 active:bg-green-600 focus-visible:outline-green-600',
            secondary: props.emoticon && !props.label ? 'bg-transparent' : 'text-default bg-neutral-100 hover:bg-neutral-200 active:shadow-inner focus-visible:outline-neutral-200',
            blue: props.emoticon && !props.label ? 'bg-transparent' : 'text-default bg-blue-300 hover:bg-blue-200 active:bg-blue-400 focus-visible:outline-blue-400',
            light: props.emoticon && !props.label ? 'bg-transparent' : 'bg-system-light text-default hover:bg-neutral-100 active:shadow-inner focus-visible:outline-neutral-100',
            dark:
                props.emoticon && !props.label
                    ? 'bg-transparent'
                    : 'bg-tertiary-800 text-default text-system-light hover:bg-tertiary-700 active:shadow-inner focus-visible:outline-tertiary-900',
            outline:
                props.emoticon && !props.label
                    ? 'bg-transparent'
                    : 'bg-transparent ring-inset ring-1 ring-gold-500/25 text-default hover:bg-gold-500/15 active:bg-gold-500/15 focus-visible:outline-gold-500/50',
            error: props.emoticon && !props.label ? 'bg-transparent' : 'bg-red-500 text-system-light hover:bg-red-400 active:bg-red-600 focus-visible:outline-red-700',
            muted:
                props.emoticon && !props.label ? 'bg-transparent' : 'text-neutral-300 bg-system-light hover:bg-neutral-100 active:text-default focus-visible:outline-neutral-100',
            transparent: 'focus-visible:outline-neutral-100' // TODO: determine transparent classes
        }

        const variantLoadingClasses = {
            primary: 'text-green-600 bg-green-300 pointer-events-none',
            secondary: 'text-sub bg-neutral-100 pointer-events-none',
            blue: 'text-blue-600 bg-blue-200 pointer-events-none',
            light: 'bg-system-light text-sub pointer-events-none',
            dark: 'bg-tertiary-600 text-system-light pointer-events-none',
            outline: 'bg-transparent text-sub pointer-events-none',
            error: 'bg-red-400 text-red-200 pointer-events-none',
            muted: 'pointer-events-none',
            transparent: 'text-sub' // TODO: determine transparent classes
        }

        const disabledClasses = props.disabled ? 'bg-neutral-100 text-inactive hover:bg-neutral-100 cursor-default active:bg-neutral-100' : ''
        const loadingClasses = props.loading && !props.disabled ? variantLoadingClasses[props.variant] : ''

        const buttonBaseClasses = [baseClasses, outlineClasses, sizeClasses[props.size], variantClasses[props.variant]]
        const buttonMergedClasses = twMerge(buttonBaseClasses, disabledClasses, loadingClasses)

        return buttonMergedClasses
    })

    const buttonPseudoClasses = computed(() => {
        const variantClasses = {
            primary: 'before:via-green-600',
            secondary: 'before:via-neutral-300',
            blue: 'before:via-blue-500',
            light: 'before:via-surface-border',
            dark: 'before:via-tertiary-900',
            outline: 'before:via-gold-500/25',
            error: 'before:via-red-600',
            muted: 'before:via-system-light',
            transparent: '' // TODO: determine transparent classes
        }

        const beforeClasses = [
            'before:content-[""] before:absolute before:aspect-[1] before:w-full before:animate-gradient-loading before:left-2/4 before:top-2/4 before:bg-[conic-gradient(var(--tw-gradient-stops))] before:bg-conic before:from-transparent before:from-25% before:to-transparent before:to-75%'
        ]
        const afterClasses = [
            'after:bg-[inherit] after:content-[""] after:absolute after:inset-[2px] after:h-[calc(100%_-_2_*_2px)] after:w-[calc(100%_-_2_*_2px)] after:rounded-[inherit]'
        ]

        return props.disabled || !props.loading ? '' : [beforeClasses, afterClasses, variantClasses[props.variant]]
    })

    const buttonClasses = computed(() => {
        const sizeClasses = {
            sm: (props.emoticon || props.icon) && !props.label ? '' : 'gap-2',
            md: (props.emoticon || props.icon) && !props.label ? '' : 'gap-3',
            lg: (props.emoticon || props.icon) && !props.label ? '' : 'gap-4',
            xl: (props.emoticon || props.icon) && !props.label ? '' : 'gap-4'
        }
        return ['text-[inherit] w-full h-full z-10 flex items-center justify-center cursor-[inherit]', sizeClasses[props.size]]
    })

    const iconClasses = computed(() => {
        const sizeClasses = {
            sm: props.label ? 'w-3' : 'w-4',
            md: props.label ? 'w-4' : 'w-5',
            lg: props.label ? 'w-4' : 'w-5',
            xl: props.label ? 'w-5' : 'w-6'
        }

        return `${sizeClasses[props.size]} flex-shrink-0`
    })

    const emoticonClasses = computed(() => {
        return props.size === 'sm' ? 'w-4' : 'w-6'
    })

    const emoticonClassesFull = computed(() => {
        return `w-full h-full`
    })
</script>
